import { useCallback } from "react";
import { Country } from "@lookiero/sty-psp-locale";
import { Segment } from "@lookiero/sty-psp-segment";
import { TrackingEventCategory, useEmitUserEvent } from "@lookiero/sty-psp-tracking";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { ProductPreview } from "../../../domain/projections/ProductPreview";
import { Constants, ItemSelectedTrackingEvent, List, PROJECT, TrackingEventName } from "./tracking";

interface TrackItemSelectedFunctionArgs {
  readonly list: List;
  readonly productPreview: ProductPreview;
  readonly lookId?: string;
}

interface TrackItemSelectedFunction {
  (args: TrackItemSelectedFunctionArgs): void;
}

interface UseTrackItemSelectedFunctionArgs {
  readonly country: Country;
  readonly segment: Segment;
  readonly userId: string;
  readonly tradename: Tradename;
}

interface UseTrackItemSelectedFunction {
  (agrs: UseTrackItemSelectedFunctionArgs): TrackItemSelectedFunction;
}

const useTrackItemSelected: UseTrackItemSelectedFunction = ({ country, segment, tradename, userId }) => {
  const emitUserEvent = useEmitUserEvent<ItemSelectedTrackingEvent>();

  const trackItemSelected: TrackItemSelectedFunction = useCallback(
    ({ list, productPreview, lookId }) => {
      emitUserEvent({
        event: TrackingEventName.ITEM_SELECTED,
        eventCategory: TrackingEventCategory.LOOK_IN,
        store: country,
        section: PROJECT,
        segment,
        tradename,
        userId,
        userUuid: userId,
        list,
        variant_id: productPreview.id,
        ecommerce: {
          detail: {
            actionField: {
              list,
            },
          },
          products: [
            {
              brand: productPreview.brand,
              category: Constants.NOT_SET,
              id: productPreview.id,
              index: 1,
              list,
              look_id: lookId || Constants.NOT_SET,
              name: productPreview.title,
              price: productPreview.price?.amount || 0,
              quantity: 1,
            },
          ],
        },
      });
    },
    [country, emitUserEvent, segment, tradename, userId],
  );

  return trackItemSelected;
};

export { useTrackItemSelected };
